<template>
  <section v-if="view.meta" class="group" @click="enter">
    <div class="group__top">
      <h3 class="group__name">{{ view.meta.name }}</h3>
      <span class="group__code" title="The group's code">{{ view.meta.code }}</span>
    </div>
    <p class="group__status">
      <span class="group__dots">
        <i v-for="m in members" :key="m.uid" class="dot" :style="{ background: m.color }" :title="m.name" />
      </span>
      {{ status }}
    </p>
    <div class="group__actions">
      <button class="primary" @click.stop="enter">{{ view.room?.solvedAt ? 'Look again' : 'Join' }}</button>
      <button class="secondary" @click.stop="sendLink">{{ copied ? 'Link copied' : 'Invite' }}</button>
      <button class="group__leave" @click.stop="leave">Leave</button>
    </div>
  </section>
</template>

<script>
// One standing group on the home screen (groups.js): its name and code,
// who's in today's room, how much of it is filled and its clock, and a
// one-tap Join.
import { formatTime, roomSeconds, roomPaused } from '@/engine/solving';
import { watchGroupDay } from '@/store/groups';
import { joinLink } from '@/store/rooms';
import { roomPercent } from '@/utils/myRooms';

export default {
  name: 'GroupCard',
  props: {
    groupId: { type: String, required: true },
    date: { type: String, required: true },
  },
  data() {
    return {
      view: { meta: null, room: null, present: 0 },
      now: Date.now(),
      copied: false,
      touch: typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches),
    };
  },
  computed: {
    members() {
      return Object.entries(this.view.meta?.members || {})
        .map(([uid, m]) => ({ uid, ...m }))
        .sort((a, b) => (a.joinedAt || 0) - (b.joinedAt || 0));
    },
    status() {
      const { room, present } = this.view;
      if (!room) return 'Nobody has started today’s yet';
      if (room.solvedAt) return `Solved together in ${formatTime(roomSeconds(room, room.solvedAt))}`;
      const clock = formatTime(roomSeconds(room, this.now));
      const pct = roomPercent(room);
      const done = pct == null ? '' : `${pct}% · `;
      if (roomPaused(room, this.now)) return `${done}Paused at ${clock}`;
      return present ? `${present} in · ${done}${clock}` : `${done || 'Under way · '}${clock}`;
    },
  },
  mounted() {
    this.unbind = watchGroupDay(this.groupId, this.date, (view) => { this.view = view; });
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
  },
  beforeUnmount() {
    if (this.unbind) this.unbind();
    clearInterval(this.ticker);
  },
  methods: {
    enter() { this.$store.dispatch('enterGroup', this.groupId); },
    async sendLink() {
      const link = joinLink(this.view.meta.code);
      if (this.touch && navigator.share) {
        try {
          await navigator.share({
            title: `${this.view.meta.name}: the daily crossword`,
            text: `Join ${this.view.meta.name} for the daily crossword: ${link} (or enter code ${this.view.meta.code})`,
          });
        } catch { /* dismissed */ }
        return;
      }
      try {
        await navigator.clipboard.writeText(link);
        this.copied = true;
      } catch { /* no clipboard */ }
    },
    leave() {
      // eslint-disable-next-line no-alert -- a deliberate, single destructive confirm
      if (window.confirm(`Leave ${this.view.meta.name}? You can rejoin with its code.`)) {
        this.$store.dispatch('leaveGroup', this.groupId);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.group { border: 1px solid var(--edge); border-radius: var(--radius); background: var(--surface); padding: 0.8rem 1rem; cursor: pointer; }
.group__top { display: flex; align-items: baseline; justify-content: space-between; gap: 0.6rem; }
.group__name { margin: 0; font-size: 1.1rem; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.group__code { font-weight: 800; letter-spacing: 0.15em; color: var(--accent); font-size: 0.85rem; }
.group__status { margin: 0.2rem 0 0; color: var(--ink-dim); font-size: 0.85rem; display: flex; align-items: center; gap: 0.5rem; font-variant-numeric: tabular-nums; }
.group__dots { display: inline-flex; gap: 3px; }
.dot { display: inline-block; width: 9px; height: 9px; border-radius: 50%; }
.group__actions { display: flex; gap: 0.5rem; margin-top: 0.6rem; align-items: center;
  .primary, .secondary { flex: 1; width: auto; } }
.group__leave { flex: 0 0 auto; background: none; border: 0; color: var(--ink-dim); font-size: 0.8rem; cursor: pointer; padding: 0 0.3rem; text-decoration: underline; }
</style>
